import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Radar Financeiro (contac/at-11) — as recomendações proativas sobre o Contac.
 *
 * ⚠️ Nenhuma função aqui engole erro (at-60), e nesta tela a consequência é mais forte que o
 * normal: o Radar mostra só o que merece atenção, então "nenhum card" é um estado LEGÍTIMO e
 * frequente. Um catch silencioso faria uma falha de rede aparecer exatamente igual a "está tudo
 * bem" — que é a leitura mais cara possível numa tela de diretoria.
 */

export interface Proveniencia {
  /** Tabelas e views consultadas, com o nome real. */
  fontes: string[];
  /** A regra do detector, com o limiar por extenso. */
  regra: string;
  /** O que o número não diz. */
  limitacoes: string[];
}

export interface RadarCard {
  detectorId: string;
  titulo: string;
  /** O número em destaque. Vem do detector determinístico, nunca da IA. */
  numero: number;
  unidade: string;
  valor: number | null;
  recomendacao: string;
  /** "template" = a IA não respondeu ou não está ligada; o texto é o automático do detector. */
  origemRedacao: 'ia' | 'template';
  proveniencia: Proveniencia;
}

export interface RadarResponse {
  cards: RadarCard[];
  competenciaBase: string;
  competenciaInicio: string;
  /** Detectores que falharam nesta carga — a tela precisa avisar, senão some no "nada a relatar". */
  detectoresComFalha: string[];
  iaConfigurada: boolean;
  /**
   * at-83: quem desativou este Radar na Administração do Sistema, e quando. `null` = ligado.
   *
   * 🔴 A tela precisa exibir um bloco PRÓPRIO para isto: com o Radar desligado nenhuma verificação
   * roda, e o estado vazio ("nada cruzou os limites — as verificações responderam normalmente")
   * seria uma afirmação falsa, e tranquilizadora, sobre consultas que não aconteceram.
   *
   * ⚠️ Opcional no tipo durante a janela de deploy: o backend antigo não devolve o campo, e a
   * ausência tem de significar "ligado", nunca quebrar a tela.
   */
  desativacao?: { por: string | null; em: string | null } | null;
}

export interface ColunaDoDetalhe {
  chave: string;
  rotulo: string;
  /** `placa` é o único tipo ACIONÁVEL: a célula abre o detalhamento mensal daquela placa. */
  tipo: 'texto' | 'numero' | 'moeda' | 'competencia' | 'placa';
}

export interface RadarDetalhe {
  detectorId: string;
  titulo: string;
  colunas: ColunaDoDetalhe[];
  itens: Record<string, string | number | null>[];
  competenciaBase: string;
  competenciaInicio: string;
  proveniencia: Proveniencia;
}

export async function buscarRecomendacoes(): Promise<RadarResponse> {
  const { data } = await axios.get<RadarResponse>(`${BACKEND_URL}/contac-recomendacoes`, {
    headers: authHeader(),
  });
  return data;
}

/** Um evento do SGA na janela analisada, para o bloco "por que este valor". */
export interface EventoDaPlaca {
  codigoEvento: number | null;
  motivo: string;
  envolvimento: string;
  eventoTipo: string;
  situacao: string;
  dataEvento: string;
  participacao: number;
}

export interface EventosDaPlaca {
  placa: string;
  baseTag: string | null;
  /**
   * ⚠️ Os três estados NÃO são intercambiáveis, e a tela precisa distinguir:
   * `sem-evento-no-periodo` é verificado ("não houve evento"); `base-sem-espelho` é ausência de
   * cobertura ("não temos como saber"). Tratar os dois como lista vazia faria a tela afirmar o que
   * não foi verificado.
   */
  cobertura: 'com-eventos' | 'sem-evento-no-periodo' | 'base-sem-espelho';
  eventos: EventoDaPlaca[];
  competenciaInicio: string;
  competenciaBase: string;
}

export async function buscarEventosDaPlaca(placa: string): Promise<EventosDaPlaca> {
  const { data } = await axios.get<EventosDaPlaca>(
    `${BACKEND_URL}/contac-recomendacoes/placa/${encodeURIComponent(placa)}/eventos`,
    { headers: authHeader() },
  );
  return data;
}

export async function buscarDetalheDaRecomendacao(detectorId: string): Promise<RadarDetalhe> {
  const { data } = await axios.get<RadarDetalhe>(
    `${BACKEND_URL}/contac-recomendacoes/${detectorId}/detalhe`,
    { headers: authHeader() },
  );
  return data;
}

/*
 * Os achados CANDIDATOS descobertos por IA saíram deste arquivo na transversal/at-82.
 *
 * `GET /contac-recomendacoes/candidatos` e `GET /contac-recomendacoes/relacionamento/candidatos`
 * deixaram de existir: os dois radares passaram a ler a mesma rota do núcleo,
 * `GET /radar/:radarId/achados-do-assistente` (`src/api/radarApi.ts`). Backend e frontend sobem
 * JUNTOS neste deploy por causa disso.
 */

// ---------------------------------------------------------------------------------------------
// Radar do RELACIONAMENTO (contac/at-13, ADR 0007 do Contac)
//
// 🔴 Nenhum tipo aqui tem valor em reais, e isso é contrato: o papel nunca vê valores (ADR 0002).
// Campo de dinheiro aparecendo nesta seção significa que a decisão foi revertida em algum lugar.
// ---------------------------------------------------------------------------------------------

export interface CardRelacionamento {
  cardId: string;
  titulo: string;
  /** Contagem de associados. Nunca um valor financeiro. */
  numero: number;
  unidade: string;
  resumo: string;
  proveniencia: Proveniencia;
}

export interface RadarRelacionamentoResponse {
  cards: CardRelacionamento[];
  /** Números fora da fila de trabalho: atraso longo (91 a 365 dias) e passivo de mais de um ano. */
  contexto: { atrasoLongo: number; legado: number };
  /** Cartões que falharam nesta carga — a tela os nomeia, nunca os esconde. */
  cardsComFalha: string[];
  dataReferencia: string;
  /**
   * Início do período aplicado, em DD/MM/AAAA.
   *
   * ⚠️ Só o INÍCIO vem do filtro da tela: o fim é sempre ontem (ADR 0008). A borda consultável do
   * Contac fica dois meses atrás, e cortar ali zeraria o cartão de quem acabou de deixar de pagar.
   * A tela precisa DECLARAR isso, senão o usuário lê a fila como se fosse do período que escolheu.
   */
  periodoInicio: string;
  /**
   * A atualização MAIS ANTIGA entre os boletos da fila — o pior caso, que é o que limita a
   * confiança. `null` = não foi possível verificar (a tela distingue de "agora").
   */
  fonteAtualizadaEm: string | null;
  /**
   * O estado da conferência na Hinova — o "Radar atualizado há X" da tela.
   *
   * `conferidoEm` é o PIOR caso (a verificação mais antiga ainda válida), nunca a mais recente.
   * Opcional no tipo porque o backend antigo não devolve o campo durante a janela de deploy.
   */
  conferencia?: {
    conferidoEm: string | null;
    associados: number;
    disponivel: boolean;
    intervaloDeHoras: number;
  };
  resumo: string;
  origemRedacao: 'ia' | 'template';
  iaConfigurada: boolean;
  parametros: Record<string, number>;
  /**
   * at-83: quem desativou o Radar do Relacionamento, e quando. `null`/ausente = ligado.
   *
   * 🔴 Aqui a distinção pesa ainda mais que no Financeiro: uma fila de cobrança vazia é lida como
   * "não há ninguém para cobrar". Com o Radar desligado, nada foi verificado — e a tela tem de
   * dizer exatamente isso.
   */
  desativacao?: { por: string | null; em: string | null } | null;
}

export interface RadarRelacionamentoDetalhe {
  cardId: string;
  titulo: string;
  colunas: ColunaDoDetalhe[];
  itens: Record<string, string | number | null>[];
  /** O total da fila. A lista vem limitada — ver `listaCortada`. */
  total: number;
  /** true = a lista foi truncada. Declarado, nunca silencioso. */
  listaCortada: boolean;
  dataReferencia: string;
  periodoInicio: string;
  /** Quantos associados da lista tiveram os boletos conferidos na Hinova. */
  conferidos: number;
  /** Quantos ficaram sem conferência (tempo, outra base ou integração desligada). */
  naoConferidos: number;
  /** Quantos saíram da lista por já terem pago — o defeito que a conferência evita. */
  removidosPorPagamento: number;
  /** false = sem credencial da Hinova neste ambiente. */
  conferenciaDisponivel: boolean;
  proveniencia: Proveniencia;
}

/**
 * O filtro que responde "de quais associados este achado fala".
 *
 * Vem PRONTO do servidor, que o montou a partir da consulta que ancorou o candidato — a tela só o
 * devolve. Ausente quando o achado não descreve uma fila listável, e aí o botão não aparece.
 */
export interface RecorteDoRelacionamento {
  dimensao?: string;
  valor?: string;
  faixa?: string;
  mes?: string;
}

/**
 * A fila de cobrança de um recorte — o "Ver associados" do achado.
 *
 * Mesma forma do detalhe de cartão, de propósito: a tela abre os dois no MESMO modal, e duplicar o
 * componente faria as duas listas divergirem no primeiro ajuste (a declaração da conferência, o
 * aviso de corte, a placa clicável).
 */
export interface RadarRelacionamentoRecorte extends Omit<RadarRelacionamentoDetalhe, 'cardId'> {
  recorte: RecorteDoRelacionamento;
  /** O recorte por extenso, como o servidor o descreve ("regional = LEGADO"). */
  descricao: string;
}

/** `?dataInicio=` só quando há período — sem ele o backend usa os últimos 365 dias. */
function comPeriodo(dataInicio?: string): Record<string, string> {
  return dataInicio ? { dataInicio } : {};
}

export async function buscarRadarRelacionamento(
  dataInicio?: string,
): Promise<RadarRelacionamentoResponse> {
  const { data } = await axios.get<RadarRelacionamentoResponse>(
    `${BACKEND_URL}/contac-recomendacoes/relacionamento`,
    { headers: authHeader(), params: comPeriodo(dataInicio) },
  );
  return data;
}

export async function buscarDetalheRadarRelacionamento(
  cardId: string,
  dataInicio?: string,
): Promise<RadarRelacionamentoDetalhe> {
  const { data } = await axios.get<RadarRelacionamentoDetalhe>(
    `${BACKEND_URL}/contac-recomendacoes/relacionamento/${encodeURIComponent(cardId)}/detalhe`,
    { headers: authHeader(), params: comPeriodo(dataInicio) },
  );
  return data;
}

/**
 * Os associados que compõem um recorte — do CARTÃO ou de um achado do assistente.
 *
 * ⚠️ O `dataInicio` precisa ser o MESMO da carga que originou o número: o recorte é o filtro, mas
 * a janela é o período — mandar outro abriria uma lista de tamanho diferente do número exibido.
 *
 * 🔴 Desde a transversal/at-82 os dois chamadores mandam coisas diferentes, e isso é a regra: o
 * cartão manda o período do filtro (ADR 0008), e o **achado do assistente não manda nada**, porque
 * a janela dele é fixa e independe do filtro da tela. Passar o período do filtro no achado
 * reintroduziria a divergência entre a lista e o número do cartão, do outro lado.
 */
export async function buscarAssociadosDoRecorte(
  recorte: RecorteDoRelacionamento,
  dataInicio?: string,
): Promise<RadarRelacionamentoRecorte> {
  const { data } = await axios.get<RadarRelacionamentoRecorte>(
    `${BACKEND_URL}/contac-recomendacoes/relacionamento/recorte/detalhe`,
    {
      headers: authHeader(),
      params: {
        ...comPeriodo(dataInicio),
        ...(recorte.dimensao ? { dimensao: recorte.dimensao } : {}),
        ...(recorte.valor ? { valor: recorte.valor } : {}),
        ...(recorte.faixa ? { faixa: recorte.faixa } : {}),
        ...(recorte.mes ? { mes: recorte.mes } : {}),
      },
    },
  );
  return data;
}

